import { useMemo } from 'react';
import classNames from 'classnames';
import { Button, Dropdown, type DropdownOption } from '@/shared/ui';
import styles from './Pagination.module.scss';
import ChevronUpIcon from '@/assets/icons/chevron_up.svg?react';

export interface PaginationProps {
  totalCount: number;
  currentPage: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  className?: string;
}

const LIMIT_OPTIONS: DropdownOption[] = [
  { value: '10', title: '10' },
  { value: '20', title: '20' },
  { value: '50', title: '50' },
  { value: '100', title: '100' },
];

export const Pagination = ({
  totalCount,
  currentPage,
  limit,
  onPageChange,
  onLimitChange,
  className,
}: PaginationProps) => {
  const totalPages = useMemo(() => {
    return Math.ceil(totalCount / limit);
  }, [totalCount, limit]);

  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Показываем все страницы, если их немного
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Всегда показываем первую страницу
      pages.push(1);

      // Вычисляем диапазон страниц вокруг текущей
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Если текущая страница близка к началу
      if (currentPage <= 3) {
        endPage = 4;
      }

      // Если текущая страница близка к концу
      if (currentPage >= totalPages - 2) {
        startPage = totalPages - 3;
      }

      // Добавляем многоточие перед диапазоном, если нужно
      if (startPage > 2) {
        pages.push('...');
      }

      // Добавляем страницы в диапазоне
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Добавляем многоточие после диапазона, если нужно
      if (endPage < totalPages - 1) {
        pages.push('...');
      }

      // Всегда показываем последнюю страницу
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  }, [currentPage, totalPages]);

  const handleLimitChange = (value: string) => {
    const newLimit = parseInt(value, 10);
    onLimitChange(newLimit);
    // При изменении лимита переходим на первую страницу
    onPageChange(1);
  };

  if (totalCount === 0) {
    return null;
  }

  return (
    <div className={classNames(styles['pagination'], className)}>
      <div className={styles['pagination-controls']}>
        <Button
          variant="secondary"
          size="small"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={styles['nav-button']}
        >
          <ChevronUpIcon style={{ transform: 'rotate(-90deg)' }} />
        </Button>

        <div className={styles['page-numbers']}>
          {pageNumbers.map((page, index) => {
            if (page === '...') {
              return (
                <span key={`ellipsis-${index}`} className={styles['ellipsis']}>
                  ...
                </span>
              );
            }

            const pageNum = page as number;
            const isActive = pageNum === currentPage;

            return (
              <Button
                key={pageNum}
                variant={isActive ? 'primary' : 'secondary'}
                size="small"
                onClick={() => onPageChange(pageNum)}
                className={styles['page-button']}
              >
                {pageNum}
              </Button>
            );
          })}
        </div>

        <Button
          variant="secondary"
          size="small"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={styles['nav-button']}
        >
          <ChevronUpIcon style={{ transform: 'rotate(90deg)' }}/>
        </Button>
      </div>

      <div className={styles['pagination-info']}>
        <Dropdown
          options={LIMIT_OPTIONS}
          value={limit.toString()}
          onChange={handleLimitChange}
          size="small"
          className={styles['limit-dropdown']}
        />
      </div>
    </div>
  );
};
